import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  Tab,
  Tabs,
  Stack,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Description as ProformaIcon,
  RequestQuote as BillIcon,
  TrendingUp as RevenuesIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useAppDispatch, useAppSelector } from '../store';
import {
  getProfile,
  updateProfile,
  uploadAvatar,
  getNotificationPreferences,
  updateNotificationPreferences,
  changePassword,
  enable2FA,
  disable2FA,
  verify2FA,
  getFirmSettings,
  updateFirmSettings,
  uploadFirmLogo,
  clearError,
} from '../store/slices/settingsSlice';
import { getCurrentUserFromStorage } from '../store/slices/authSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    profile,
    notifications,
    firm,
    loading,
    error,
    profileLoading,
    notificationsLoading,
    firmLoading,
    passwordLoading,
    twoFactorLoading,
    avatarUploading,
    logoUploading,
    twoFactorEnabled,
  } = useAppSelector((state) => state.settings);

  const [tabValue, setTabValue] = useState('profile');
  const { mode, toggleColorMode } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: '',
    role: '',
  });
  const [notificationsForm, setNotificationsForm] = useState({
    email_notifications: false,
    case_updates: false,
    task_reminders: false,
    billing_alerts: false,
    chat_messages: false,
    weekly_report: false,
  });
  const [firmForm, setFirmForm] = useState({
    settings: [{
       id: 0,
       key: '',
       value: '',
       created_at: '',
       updated_at: '',
       firm_id: 0,
       description: ''
    }],
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    default_currency: '',
    tax_rate: '',
    company_name: '',
    company_address: '',
    invoice_prefix: '',
    invoice_next_number: '',
    enable_payment_reminders: false,
    bank_details: '',   
    bill_footer: '',   
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const currentUser = getCurrentUserFromStorage();
  const isAdmin = currentUser?.role?.name === 'super-admin';
  const userId = currentUser?.user_id;

  useEffect(() => {
    if (userId) {
      dispatch(getProfile(userId));
      dispatch(getNotificationPreferences(userId));
      if (!isAdmin) {
        dispatch(getFirmSettings());
      }
    }
  }, [dispatch, userId, isAdmin]);

  useEffect(() => {
    if (profile) {
      setProfileForm({
        full_name: profile.full_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        position: profile.position?.name || '',
        role: profile.role?.name || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (notifications) {
      setNotificationsForm(notifications);
    }
  }, [notifications]);

  useEffect(() => {
    if (firm && !isAdmin) {
      // Extract settings from the settings array
      const settingsMap = firm.settings?.reduce((acc: any, setting: any) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {}) || {};

      setFirmForm({
        settings: firm.settings || [],
        name: firm.name || '',
        address: firm.address || '',
        city: firm.city || '',
        state: firm.state || '',
        country: firm.country || '',
        postal_code: firm.postal_code || '',
        phone: firm.phone || '',
        email: firm.email || '',
        website: firm.website || '',
        description: firm.description || '',
        default_currency: settingsMap.default_currency || firm.default_currency || '',
        tax_rate: settingsMap.tax_rate || firm.tax_rate?.toString() || '',
        company_name: settingsMap.company_name || firm.company_name || '',
        company_address: settingsMap.company_address || firm.company_address || '',
        invoice_prefix: settingsMap.invoice_prefix || firm.invoice_prefix || '',
        invoice_next_number: settingsMap.invoice_next_number || firm.invoice_next_number?.toString() || '',
        enable_payment_reminders: settingsMap.enable_payment_reminders ? settingsMap.enable_payment_reminders === '1' : (firm.enable_payment_reminders ?? false),
        bank_details: settingsMap.bank_details || '',
        bill_footer: settingsMap.bill_footer || '',
      });
    }
  }, [firm, isAdmin]);

  // Reset tab if admin tries to access a firm-only tab
  useEffect(() => {
    if (isAdmin && (tabValue === 'cabinet' || tabValue === 'billing')) {
      setTabValue('profile');
    }
  }, [isAdmin, tabValue]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    try {
      // Role isn't self-editable here (it's shown for reference only — see
      // the disabled field below) and the backend rejects the update when
      // it's sent blank/mismatched, which is what was causing every save
      // to fail with "Failed to update profile".
      const { role, ...editableFields } = profileForm;
      await dispatch(updateProfile({ userId, data: editableFields })).unwrap();
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      await dispatch(uploadAvatar({ userId, file: formData })).unwrap();
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handleNotificationsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    try {
      await dispatch(updateNotificationPreferences({ userId, data: notificationsForm })).unwrap();
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || passwordForm.new_password !== passwordForm.confirm_password) return;

    try {
      await dispatch(changePassword({
        userId,
        data: {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        },
      })).unwrap();
      
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handle2FAToggle = async () => {
    if (!userId) return;

    if (twoFactorEnabled) {
      try {
        await dispatch(disable2FA(userId)).unwrap();
      } catch (error) {
        // Error is handled by Redux state
      }
    } else {
      try {
        await dispatch(enable2FA(userId)).unwrap();
        setTwoFactorDialogOpen(true);
      } catch (error) {
        // Error is handled by Redux state
      }
    }
  };

  const handle2FAVerify = async () => {
    if (!userId) return;

    try {
      await dispatch(verify2FA({ userId, code: twoFactorCode })).unwrap();
      setTwoFactorDialogOpen(false);
      setTwoFactorCode('');
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handleFirmSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Build the settings array from form data
      const existingSettings = (firmForm.settings || []).filter((setting: any) => 
        !['bank_details', 'bill_footer', 'company_name', 'company_address', 'default_currency', 'tax_rate', 'invoice_prefix', 'invoice_next_number', 'enable_payment_reminders'].includes(setting.key)
      );

      const newSettings = [
        {
          id: firmForm.settings?.find((s: any) => s.key === 'company_name')?.id || 0,
          key: 'company_name',
          value: firmForm.company_name,
          description: 'Official company name for invoices',
          created_at: firmForm.settings?.find((s: any) => s.key === 'company_name')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'company_address')?.id || 0,
          key: 'company_address',
          value: firmForm.company_address,
          description: 'Official company address for invoices',
          created_at: firmForm.settings?.find((s: any) => s.key === 'company_address')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'default_currency')?.id || 0,
          key: 'default_currency',
          value: firmForm.default_currency,
          description: 'The default currency for this firm (e.g., USD, EUR, GBP)',
          created_at: firmForm.settings?.find((s: any) => s.key === 'default_currency')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'tax_rate')?.id || 0,
          key: 'tax_rate',
          value: firmForm.tax_rate,
          description: 'Default tax/VAT rate for the firm (%)',
          created_at: firmForm.settings?.find((s: any) => s.key === 'tax_rate')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'invoice_prefix')?.id || 0,
          key: 'invoice_prefix',
          value: firmForm.invoice_prefix,
          description: 'Prefix for invoice numbers (e.g., INV-)',
          created_at: firmForm.settings?.find((s: any) => s.key === 'invoice_prefix')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'invoice_next_number')?.id || 0,
          key: 'invoice_next_number',
          value: firmForm.invoice_next_number,
          description: 'Next invoice number to use',
          created_at: firmForm.settings?.find((s: any) => s.key === 'invoice_next_number')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'enable_payment_reminders')?.id || 0,
          key: 'enable_payment_reminders',
          value: firmForm.enable_payment_reminders ? '1' : '0',
          description: 'Whether to send automatic payment reminders',
          created_at: firmForm.settings?.find((s: any) => s.key === 'enable_payment_reminders')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'bank_details')?.id || 0,
          key: 'bank_details',
          value: firmForm.bank_details,
          description: 'Firm bank details',
          created_at: firmForm.settings?.find((s: any) => s.key === 'bank_details')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        },
        {
          id: firmForm.settings?.find((s: any) => s.key === 'bill_footer')?.id || 0,
          key: 'bill_footer',
          value: firmForm.bill_footer,
          description: 'Footer to use on bills/invoices',
          created_at: firmForm.settings?.find((s: any) => s.key === 'bill_footer')?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          firm_id: firm?.id || 0,
        }
      ];

      const settings = [...existingSettings, ...newSettings];

      await dispatch(updateFirmSettings({
        name: firmForm.name,
        address: firmForm.address,
        city: firmForm.city,
        state: firmForm.state,
        country: firmForm.country,
        postal_code: firmForm.postal_code,
        phone: firmForm.phone,
        email: firmForm.email,
        website: firmForm.website,
        description: firmForm.description,
        settings,
      })).unwrap();
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handleFirmLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      await dispatch(uploadFirmLogo(formData)).unwrap();
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const getInitials = (full_name: string) => {
    return `${full_name?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>{t('navigation.settings')}</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Card sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, height: 'fit-content' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant="scrollable"
            sx={{
              // `alignItems` only controls the icon/label's vertical
              // centering — the icon+label row itself was still centered
              // horizontally by MUI's default `justify-content: center`,
              // so each tab's content sat wherever its (varying-width)
              // label happened to center it, producing the staggered/
              // zig-zag look. `justifyContent: flex-start` plus a full-width
              // root pins every tab's icon to the same left edge.
              '& .MuiTab-root': {
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                minHeight: 48,
                width: '100%',
              },
            }}
          >
            {!isAdmin && <Tab value="cabinet" icon={<BusinessIcon />} iconPosition="start" label={t('settings.firm')} />}
            <Tab value="appearance" icon={<PaletteIcon />} iconPosition="start" label={t('settings.appearance')} />
            {!isAdmin && <Tab value="billing" icon={<ReceiptIcon />} iconPosition="start" label={t('navigation.billing')} />}
            <Tab value="notifications" icon={<NotificationsIcon />} iconPosition="start" label={t('settings.notifications')} />
            <Tab value="profile" icon={<PersonIcon />} iconPosition="start" label={t('common.profile')} />
            <Tab value="security" icon={<SecurityIcon />} iconPosition="start" label={t('settings.security')} />
          </Tabs>
        </Card>

        <Card sx={{ flex: 1, minWidth: 0 }}>
        <CardContent>
          {/* Profile Tab */}
          <TabPanel value={tabValue} index="profile">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
              <Avatar
                sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: 'primary.main' }}
                src={profile?.avatar}
              >
                {getInitials(profileForm.full_name)}
              </Avatar>
              <Box>
                <Typography variant="h6">{profileForm.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {profileForm.position} - {profileForm.role}
                </Typography>
                <Button
                  size="small"
                  component="label"
                  sx={{ mt: 1 }}
                  disabled={avatarUploading}
                >
                  {avatarUploading ? t('common.uploading') : t('settings.changePhoto')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleAvatarUpload}
                  />
                </Button>
              </Box>
            </Box>
            <Box component="form" onSubmit={handleProfileSubmit}>
              <Stack spacing={2} sx={{ maxWidth: 600 }}>
                <TextField
                  label={t('users.fullName')}
                  fullWidth
                  type="text"
                  value={profileForm.full_name}
                  onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                />
                <TextField
                  label={t('common.email')}
                  fullWidth
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('common.phone')}
                    fullWidth
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  />
                  <TextField
                    label={t('users.role')}
                    fullWidth
                    value={profileForm.role}
                    disabled
                    helperText={t('settings.roleNotSelfEditable', 'Contact an admin to change your role')}
                  />
                </Box>
                <TextField
                  label={t('common.position')}
                  fullWidth
                  value={profileForm.position}
                  onChange={(e) => setProfileForm({ ...profileForm, position: e.target.value })}
                />
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={profileLoading}
                  >
                    {profileLoading ? t('common.saving') : t('common.saveChanges')}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel value={tabValue} index="notifications">
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.notificationSettings')}</Typography>
            <Box component="form" onSubmit={handleNotificationsSubmit}>
              <Stack spacing={1} sx={{ maxWidth: 500 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsForm.email_notifications}
                      onChange={(e) => setNotificationsForm({ ...notificationsForm, email_notifications: e.target.checked })}
                    />
                  }
                  label={t('settings.emailNotifications')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsForm.case_updates}
                      onChange={(e) => setNotificationsForm({ ...notificationsForm, case_updates: e.target.checked })}
                    />
                  }
                  label={t('settings.caseStatusUpdates')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsForm.task_reminders}
                      onChange={(e) => setNotificationsForm({ ...notificationsForm, task_reminders: e.target.checked })}
                    />
                  }
                  label={t('settings.taskReminders')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsForm.billing_alerts}
                      onChange={(e) => setNotificationsForm({ ...notificationsForm, billing_alerts: e.target.checked })}
                    />
                  }
                  label={t('settings.billingAlerts')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsForm.chat_messages}
                      onChange={(e) => setNotificationsForm({ ...notificationsForm, chat_messages: e.target.checked })}
                    />
                  }
                  label={t('settings.chatMessages')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsForm.weekly_report}
                      onChange={(e) => setNotificationsForm({ ...notificationsForm, weekly_report: e.target.checked })}
                    />
                  }
                  label={t('settings.weeklySummaryReport')}
                />
                <Box sx={{ pt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={notificationsLoading}
                  >
                    {notificationsLoading ? t('common.saving') : t('settings.savePreferences')}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </TabPanel>

          {/* Security Tab */}
          <TabPanel value={tabValue} index="security">
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.changePassword')}</Typography>
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <Stack spacing={2} sx={{ maxWidth: 400 }}>
                <TextField
                  label={t('settings.currentPassword')}
                  type={showPassword.current ? 'text' : 'password'}
                  fullWidth
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                          edge="end"
                        >
                          {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t('settings.newPassword')}
                  type={showPassword.new ? 'text' : 'password'}
                  fullWidth
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                          edge="end"
                        >
                          {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t('settings.confirmNewPassword')}
                  type={showPassword.confirm ? 'text' : 'password'}
                  fullWidth
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  error={passwordForm.new_password !== passwordForm.confirm_password && passwordForm.confirm_password !== ''}
                  helperText={passwordForm.new_password !== passwordForm.confirm_password && passwordForm.confirm_password !== '' ? t('users.passwordsDoNotMatch') : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                          edge="end"
                        >
                          {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={passwordLoading || passwordForm.new_password !== passwordForm.confirm_password}
                  >
                    {passwordLoading ? t('common.updating') : t('settings.updatePassword')}
                  </Button>
                </Box>
              </Stack>
            </Box>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.twoFactorAuth')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('settings.twoFactorAuthDescription')}
            </Typography>
            <Button
              variant={twoFactorEnabled ? "outlined" : "contained"}
              onClick={handle2FAToggle}
              disabled={twoFactorLoading}
            >
              {twoFactorLoading ? t('common.processing') : twoFactorEnabled ? t('settings.disable2FA') : t('settings.enable2FA')}
            </Button>
          </TabPanel>

          {/* Firm Tab */}
          {!isAdmin && <TabPanel value={tabValue} index="cabinet">
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.firm')}</Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {t('settings.organizationSettingsHint')}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate('/settings/organization')}
                  sx={{ ml: 1, textTransform: 'none' }}
                >
                  {t('navigation.organizationSettings')}
                </Button>
              </Typography>
            </Alert>

            {/* Logo Preview */}
            {firm?.logo_url && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>{t('settings.currentLogo')}</Typography>
                <Box
                  component="img"
                  src={firm.logo_url}
                  alt={t('settings.firmLogoAlt')}
                  sx={{ maxWidth: 200, maxHeight: 100, borderRadius: 1 }}
                />
              </Box>
            )}

            <Box component="form" onSubmit={handleFirmSubmit}>
              <Stack spacing={2} sx={{ maxWidth: 600 }}>
                {/* Basic Information */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{t('settings.basicInformation')}</Typography>
                <TextField
                  label={t('firms.firmName')}
                  fullWidth
                  value={firmForm.name}
                  onChange={(e) => setFirmForm({ ...firmForm, name: e.target.value })}
                />
                <TextField
                  label={t('common.description')}
                  fullWidth
                  multiline
                  rows={3}
                  value={firmForm.description}
                  onChange={(e) => setFirmForm({ ...firmForm, description: e.target.value })}
                />
                <TextField
                  label={t('firms.website')}
                  fullWidth
                  type="url"
                  value={firmForm.website}
                  onChange={(e) => setFirmForm({ ...firmForm, website: e.target.value })}
                />

                {/* Contact Information */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{t('settings.contactInformation')}</Typography>
                <TextField
                  label={t('common.email')}
                  fullWidth
                  type="email"
                  value={firmForm.email}
                  onChange={(e) => setFirmForm({ ...firmForm, email: e.target.value })}
                />
                <TextField
                  label={t('common.phone')}
                  fullWidth
                  value={firmForm.phone}
                  onChange={(e) => setFirmForm({ ...firmForm, phone: e.target.value })}
                />

                {/* Address Information */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{t('common.address')}</Typography>
                <TextField
                  label={t('settings.streetAddress')}
                  fullWidth
                  value={firmForm.address}
                  onChange={(e) => setFirmForm({ ...firmForm, address: e.target.value })}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('clients.city')}
                    fullWidth
                    value={firmForm.city}
                    onChange={(e) => setFirmForm({ ...firmForm, city: e.target.value })}
                  />
                  <TextField
                    label={t('settings.stateProvince')}
                    fullWidth
                    value={firmForm.state}
                    onChange={(e) => setFirmForm({ ...firmForm, state: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('settings.countryCode')}
                    fullWidth
                    placeholder={t('settings.countryCodePlaceholder')}
                    value={firmForm.country}
                    onChange={(e) => setFirmForm({ ...firmForm, country: e.target.value })}
                  />
                  <TextField
                    label={t('settings.postalCode')}
                    fullWidth
                    value={firmForm.postal_code}
                    onChange={(e) => setFirmForm({ ...firmForm, postal_code: e.target.value })}
                  />
                </Box>

                {/* Company Details */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{t('settings.companyDetails')}</Typography>
                <TextField
                  label={t('settings.companyName')}
                  fullWidth
                  value={firmForm.company_name}
                  onChange={(e) => setFirmForm({ ...firmForm, company_name: e.target.value })}
                />
                <TextField
                  label={t('settings.companyAddress')}
                  fullWidth
                  value={firmForm.company_address}
                  onChange={(e) => setFirmForm({ ...firmForm, company_address: e.target.value })}
                />

                {/* Billing Details */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
                  {t('settings.billingDetails')}
                </Typography>

                <TextField
                  label={t('settings.bankDetails')}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={t('settings.bankDetailsPlaceholder')}
                  value={firmForm.bank_details}
                  onChange={(e) =>
                    setFirmForm({ ...firmForm, bank_details: e.target.value })
                  }
                  helperText={t('settings.bankDetailsHelper')}
                />

                <TextField
                  label={t('settings.invoiceFooter')}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t('settings.invoiceFooterPlaceholder')}
                  value={firmForm.bill_footer}
                  onChange={(e) =>
                    setFirmForm({ ...firmForm, bill_footer: e.target.value })
                  }
                  helperText={t('settings.invoiceFooterHelper')}
                />

                {/* Financial Settings */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{t('settings.financialSettings')}</Typography>
                <TextField
                  select
                  label={t('settings.defaultCurrency')}
                  fullWidth
                  value={firmForm.default_currency}
                  onChange={(e) => setFirmForm({ ...firmForm, default_currency: e.target.value })}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="CAD">CAD</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                  <MenuItem value="INR">INR</MenuItem>
                  <MenuItem value="XOF">XOF</MenuItem>
                  <MenuItem value="XAF">XAF</MenuItem>
                  <MenuItem value="GHS">GHS</MenuItem>
                  <MenuItem value="NGN">NGN</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                </TextField>
                <TextField
                  label={t('settings.taxRate')}
                  type="number"
                  fullWidth
                  value={firmForm.tax_rate}
                  onChange={(e) => setFirmForm({ ...firmForm, tax_rate: e.target.value })}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />

                {/* Invoice Settings */}
                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{t('settings.invoiceSettings')}</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label={t('settings.invoicePrefix')}
                    fullWidth
                    value={firmForm.invoice_prefix}
                    onChange={(e) => setFirmForm({ ...firmForm, invoice_prefix: e.target.value })}
                  />
                  <TextField
                    label={t('settings.nextInvoiceNumber')}
                    fullWidth
                    type="number"
                    value={firmForm.invoice_next_number}
                    onChange={(e) => setFirmForm({ ...firmForm, invoice_next_number: e.target.value })}
                    inputProps={{ min: 1 }}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={firmForm.enable_payment_reminders}
                      onChange={(e) => setFirmForm({ ...firmForm, enable_payment_reminders: e.target.checked })}
                    />
                  }
                  label={t('settings.enablePaymentReminders')}
                />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={firmLoading}
                  >
                    {firmLoading ? t('common.saving') : t('settings.saveFirmSettings')}
                  </Button>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    disabled={logoUploading}
                  >
                    {logoUploading ? t('common.uploading') : t('settings.uploadLogo')}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFirmLogoUpload}
                    />
                  </Button>
                </Box>
              </Stack>
            </Box>
          </TabPanel>}

          {/* Appearance Tab */}
          <TabPanel value={tabValue} index="appearance">
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.appearance')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings.currentTheme')} <strong>{mode === 'dark' ? t('settings.dark') : t('settings.light')}</strong>
            </Typography>
            <Button
              variant="outlined"
              startIcon={mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              onClick={toggleColorMode}
            >
              {t('settings.switchToMode', { mode: mode === 'dark' ? t('settings.light') : t('settings.dark') })}
            </Button>
          </TabPanel>

          {/* Facturation (Billing) Tab */}
          {!isAdmin && (
          <TabPanel value={tabValue} index="billing">
            <Typography variant="h6" sx={{ mb: 2 }}>{t('navigation.billing')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings.billingTabDescription', { firmTab: t('settings.firm') })}
            </Typography>
            <Stack spacing={1.5} sx={{ maxWidth: 360 }}>
              <Button
                variant="outlined"
                startIcon={<ProformaIcon />}
                onClick={() => navigate('/billing/proforma')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('cases.proformaQuotations')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<BillIcon />}
                onClick={() => navigate('/billing/bills')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('navigation.bills')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RevenuesIcon />}
                onClick={() => navigate('/billing/revenues')}
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('navigation.revenues')}
              </Button>
            </Stack>
          </TabPanel>
          )}
        </CardContent>
        </Card>
      </Box>

      {/* 2FA Verification Dialog */}
      <Dialog open={twoFactorDialogOpen} onClose={() => setTwoFactorDialogOpen(false)}>
        <DialogTitle>{t('settings.verifyTwoFactorAuth')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('settings.enterVerificationCode')}
          </Typography>
          <TextField
            fullWidth
            label={t('settings.verificationCode')}
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFactorDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handle2FAVerify}
            variant="contained"
            disabled={twoFactorLoading || !twoFactorCode}
          >
            {twoFactorLoading ? t('common.verifying') : t('common.verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
